import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useDeps } from 'src/hooks/useDeps';
import { useConstant } from 'src/core/hooks/react-hooks';
import { BehaviorSubject } from 'rxjs';
import { distinctUntilChanged, tap } from 'rxjs';
import { ApolloClient } from '@apollo/client';
import { isVatCookieValue, VAT_COOKIE, VAT_STORAGE_KEY } from 'src/components/AppShell/app-shell.types';
import { cookieExpiresInDays, useResolvedCookies } from 'src/util/cookies';

import { useSearch } from '../../hooks/router-hooks';

import { ConfigContext } from './ConfigProvider';
import { setCookie } from './ConfigProvider';
const noop = (name: string) => {
    return () => {
        // eslint-disable-next-line no-console
        console.log(`${name} is missing - did you include the register function for it?`);
        return undefined;
    };
};

type Props = Record<string, unknown>;

export const VatContext = React.createContext<{
    vat$: BehaviorSubject<boolean>;
    setVat: (vat: boolean) => void;
}>({
    vat$: new BehaviorSubject<boolean>(false),
    setVat: noop('set vat'),
});

export const VatProvider: React.FC<React.PropsWithChildren> = React.memo((props) => {
    const search = useSearch();
    const isVatFromURL = Boolean(new URLSearchParams(search).get('vat'));
    const { storage, client } = useDeps();

    const { setCurrency } = useContext(ConfigContext);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const params = new URLSearchParams(window.location.search);
        const debugCountry = params.get('debug-country') || params.get('store');
        const allowedCountries = ['ie', 'uk'];

        if (debugCountry && allowedCountries.includes(debugCountry.toLowerCase())) {
            // Capture the `cur` param before stripping the query string so an explicit
            // currency (e.g. ?store=ie&cur=gbp) is preserved instead of the store default.
            const currencyParam = params.get('cur') || undefined;
            const cleanUrl = window.location.origin + window.location.pathname;
            window.history.replaceState({}, document.title, cleanUrl);

            if (client) {
                (client as ApolloClient<any>).clearStore();
            }

            if (debugCountry.toLowerCase() === 'ie') {
                setCookie('isIrishUser', 'true', setCurrency, true, currencyParam);
            } else if (debugCountry.toLowerCase() === 'uk') {
                setCookie('isIrishUser', 'false', setCurrency, true, currencyParam);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Read from the request cookies on the server and document.cookie in the browser,
    // so both sides start from the same value and hydration matches.
    const cookies = useResolvedCookies();
    const initial = isVatFromURL || isVatCookieValue(cookies.get(VAT_COOKIE, { doNotParse: true, doNotUpdate: true }));

    const vat$ = useConstant(() => new BehaviorSubject(initial));

    const setVat = useCallback(
        (vat: boolean) => {
            vat$.next(vat);
            /**
             * Note: this is NOT a value being sent to redux
             * like normal as we would with the cart/user
             * etc because this value is simply not important enough.
             * If it goes away then it defaults to false anyway, so no issue.
             * Only set while true: the SSR cache is bypassed for inc VAT requests.
             */
            if (vat) {
                cookies.set(VAT_COOKIE, 'true', cookieExpiresInDays(365));
            } else {
                cookies.remove(VAT_COOKIE, { path: '/' });
            }
        },
        [cookies, vat$],
    );

    // Sync after commit: emitting from render made vat$ subscribers setState mid-render.
    // vat$ already starts at `initial`, so SSR and the first client render are unaffected.
    useEffect(() => {
        setVat(initial);
    }, [initial, setVat]);

    // Carry over the preference from before it moved to a cookie. Runs after hydration,
    // so these users see exc VAT prices flip to inc VAT once, then SSR handles it.
    useEffect(() => {
        if (storage.get(VAT_STORAGE_KEY) === true) {
            setVat(true);
        }
        storage.remove(VAT_STORAGE_KEY);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const api = useMemo(() => {
        return { vat$, setVat };
    }, [setVat, vat$]);

    return <VatContext.Provider value={api}>{props.children}</VatContext.Provider>;
});

/**
 * Use this when you want a change in VAT to cause your components to re-render
 */
export function useVatCausesRender(): { vat: boolean; setVat: (vat: boolean) => void } {
    const { vat$, setVat } = useContext(VatContext);
    const [vat, setVatLocal] = useState(vat$.value);
    useEffect(() => {
        const sub = vat$.pipe(distinctUntilChanged(), tap(setVatLocal)).subscribe();
        return () => sub.unsubscribe();
    }, [vat$]);

    return { vat, setVat };
}
